const GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const GOOGLE_USERINFO_URL = "https://www.googleapis.com/oauth2/v3/userinfo";

let googleIdentityScriptPromise;

const toGoogleAuthError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const loadGoogleIdentityScript = () => {
  if (
    typeof window !== "undefined" &&
    window.google?.accounts?.oauth2
  ) {
    return Promise.resolve(window.google);
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(
        toGoogleAuthError(
          "unavailable",
          "Google sign in is only available in the browser.",
        ),
      );
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`,
    );

    const handleLoad = () => {
      if (window.google?.accounts?.oauth2) {
        resolve(window.google);
        return;
      }

      reject(
        toGoogleAuthError(
          "script_failed",
          "Google sign in could not be loaded.",
        ),
      );
    };

    const handleError = () => {
      reject(
        toGoogleAuthError(
          "script_failed",
          "Google sign in could not be loaded.",
        ),
      );
    };

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_IDENTITY_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.head.appendChild(script);
  }).catch((error) => {
    googleIdentityScriptPromise = undefined;
    throw error;
  });

  return googleIdentityScriptPromise;
};

const fetchGoogleUserProfile = async (accessToken) => {
  const response = await fetch(GOOGLE_USERINFO_URL, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw toGoogleAuthError(
      "profile_failed",
      "Google profile could not be loaded.",
    );
  }

  const profile = await response.json();

  return {
    email: profile?.email || "",
    firstName: profile?.given_name || "",
    lastName: profile?.family_name || "",
    given_name: profile?.given_name || "",
    family_name: profile?.family_name || "",
    avatar: profile?.picture || "",
    profile_pic: profile?.picture || "",
    googleSub: profile?.sub || "",
    provider: "google",
    email_verified: Boolean(profile?.email_verified),
    accessToken,
  };
};

export const requestGoogleProfile = async ({ clientId }) => {
  if (!clientId) {
    throw toGoogleAuthError(
      "missing_client_id",
      "Google sign in is not configured for this environment.",
    );
  }

  await loadGoogleIdentityScript();

  return new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: "openid email profile",
      callback: async (tokenResponse) => {
        if (tokenResponse?.error) {
          reject(
            toGoogleAuthError(
              tokenResponse.error,
              "Google sign in could not be completed.",
            ),
          );
          return;
        }

        try {
          const profile = await fetchGoogleUserProfile(
            tokenResponse.access_token,
          );
          resolve(profile);
        } catch (error) {
          reject(error);
        }
      },
      error_callback: (error) => {
        const errorCode =
          error?.type === "popup_closed"
            ? "cancelled"
            : error?.type || "popup_failed";
        const errorMessage =
          error?.type === "popup_closed"
            ? "Google sign in was cancelled."
            : "Google sign in could not be completed.";

        reject(toGoogleAuthError(errorCode, errorMessage));
      },
    });

    tokenClient.requestAccessToken({
      prompt: "select_account",
    });
  });
};
